import type { FetchPhysicalAddressQuery } from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import { CountryCodes, type Jurisdictions } from '@onevesthq/ov-enums';
import { extractPostalCodeFromText, isCompletePostalCode } from './postalCode';

export type CanadaPostLookupResult = NonNullable<
  NonNullable<
    FetchPhysicalAddressQuery['fetchPhysicalAddress']
  >['addressDetails']
>;

export const DEFAULT_CANADA_POST_COUNTRIES: CountryCodes[] = [CountryCodes.CA];

export const JURISDICTION_COUNTRIES: readonly CountryCodes[] = [
  CountryCodes.CA,
  CountryCodes.US,
];

const hasJurisdiction = (country?: string) =>
  !!country && JURISDICTION_COUNTRIES.includes(country as CountryCodes);

export const getInitialCountry = (
  supportedCountries: CountryCodes[],
  addressCountry?: CountryCodes,
) =>
  supportedCountries.find((country) => country === addressCountry) ??
  supportedCountries[0] ??
  CountryCodes.CA;

export const resolveLookupCountry = (result: CanadaPostLookupResult) =>
  result.CountryIso2 ?? result.country ?? undefined;

const resolveJurisdiction = (
  result: CanadaPostLookupResult,
  country?: string,
) => {
  if (result.jurisdiction) return result.jurisdiction as Jurisdictions;
  if (!hasJurisdiction(country) || !result.ProvinceCode) return undefined;
  return `${country}_${result.ProvinceCode}` as Jurisdictions;
};

// The postcode check only understands CA/US formats, so it would drop valid foreign postcodes like AU `2000`.
const resolvePostal = (
  result: CanadaPostLookupResult,
  suggestionText: string,
  country?: string,
) => {
  const postal = result.postal ?? undefined;
  if (!hasJurisdiction(country)) {
    return postal || extractPostalCodeFromText(suggestionText);
  }
  return isCompletePostalCode(postal)
    ? postal
    : extractPostalCodeFromText(suggestionText);
};

export const mapCanadaPostLookupResult = (
  result: CanadaPostLookupResult,
  suggestionText: string,
): Partial<PhysicalAddress> => {
  const country = resolveLookupCountry(result);
  return {
    country: country as PhysicalAddress['country'],
    jurisdiction: resolveJurisdiction(result, country),
    houseNumber: result.houseNumber ?? undefined,
    unitNumber: result.unitNumber ?? undefined,
    streetName: result.streetName ?? undefined,
    city: result.city ?? undefined,
    postal: resolvePostal(result, suggestionText, country),
    neighborhood: result.neighborhood ?? undefined,
  };
};
